/* eslint-disable import/prefer-default-export */

import { FloatingTree, Placement } from '@floating-ui/react'
import { FC, MouseEvent, useCallback } from 'react'

import { useRoadmapsForOption } from '~entities/roadmap'
import RoadmapIcon from '~shared/icons/16/roadmap.svg'
import Button from '~shared/ui/Button'
import Flex from '~shared/ui/Flex'
import Menu from '~shared/ui/Menu'
import Popup, { usePopup } from '~shared/ui/Popup'
import Spinner from '~shared/ui/Spinner'

interface ProductIdeaPromoteToRoadmapCTAProps extends React.HTMLAttributes<HTMLElement> {
  onRoadmapClick: (roadmapId: string) => void
  opened?: boolean
  defaultOpened?: boolean
  onOpenedChange?: (state: boolean) => void
  loading?: boolean
  disabled?: boolean
  placement?: Placement
  portalRoot?: HTMLElement | React.MutableRefObject<HTMLElement>
}

export const ProductIdeaPromoteToRoadmapCTA: FC<ProductIdeaPromoteToRoadmapCTAProps> = ({
  onRoadmapClick,
  opened,
  defaultOpened,
  onOpenedChange,
  loading: updateLoading,
  disabled,
  placement = 'bottom-start',
  portalRoot,
  ...props
}) => {
  const { isOpened, nodeId, context, refs, floatingStyles, getReferenceProps, getFloatingProps } = usePopup({
    placement,
    offset: 10,
    opened,
    defaultOpened,
    onOpenedChange,
  })
  const [options, loading] = useRoadmapsForOption({})

  const handleOptionClick = useCallback(
    (ev: MouseEvent<HTMLElement>) => {
      ev.stopPropagation()
      onRoadmapClick(ev.currentTarget.dataset.value)
    },
    [onRoadmapClick]
  )

  return (
    <>
      <Button
        ref={refs.setReference}
        variant="primary"
        size="sm"
        {...getReferenceProps(props)}
        disabled={disabled}
        loading={updateLoading}
      >
        Promote to roadmap
      </Button>

      <FloatingTree>
        <Popup
          ref={refs.setFloating}
          nodeId={nodeId}
          opened={isOpened}
          context={context}
          style={floatingStyles}
          portalRoot={portalRoot}
          {...getFloatingProps()}
        >
          <Menu>
            <Menu.List>
              <Menu.Heading>Select a roadmap</Menu.Heading>
              {options.map((option) => (
                <Menu.Item before={<RoadmapIcon />} key={option.id} data-value={option.id} onClick={handleOptionClick}>
                  {option.name}
                </Menu.Item>
              ))}
              {loading && (
                <Flex align="center" justify="center" className="w-full">
                  <Spinner />
                </Flex>
              )}
            </Menu.List>
          </Menu>
        </Popup>
      </FloatingTree>
    </>
  )
}
